/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
	Box,
	Paper,
	Typography,
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Button,
	Tooltip,
	FormControlLabel,
	Switch,
	Divider,
	Badge,
	ButtonGroup,
	Collapse,
} from '@mui/material';
import {
	FilterList as FilterListIcon,
	Clear as ClearIcon,
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import DatePicker from '@mui/lab/DatePicker';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { SalesOrderFiltersState, SalesOrderFiltersProps } from './types';
import { convertSalesOrderFiltersToEndpoint } from './helpers';
import { createMenuItems, getDefaultFilterValue } from './utils';
import { segmentTypes, serviceTypes, saleOrderInvoicingStatusTypes } from '../../../types';
import { fetchUserSummaryCached } from '../../../userSummaryCache';
import { IUserSummary } from '../../../types';

const defaultFilters: SalesOrderFiltersState = {
	onlyMine: false,
	createdBy: '',
	dealId: '',
	segmentType: '',
	invoicingStatuses: [],
	hasPendingPayAmount: null,
	customerEmpty: null,
	mainPassengerCompleteName: '',
	customerCompleteName: '',
	createdDatePreset: '',
	createdDateFrom: null,
	createdDateTo: null,
	travelDepartureDatePreset: '',
	travelDepartureDateFrom: null,
	travelDepartureDateTo: null,
	travelReturnDatePreset: '',
	travelReturnDateFrom: null,
	travelReturnDateTo: null,
	salesAmountFrom: '',
	salesAmountTo: '',
	totalCostFrom: '',
	totalCostTo: '',
	grossProfitFrom: '',
	grossProfitTo: '',
	chargedAmountFrom: '',
	chargedAmountTo: '',
	pendingPayAmountFrom: '',
	pendingPayAmountTo: '',
	orderNumber: '',
	quotationId: '',
	destinations: '',
	clientReference: '',
	servicesTypes: [],
	channelId: '',
	isBitrixDeal: null,
	isReturnDateReached: null,
	updatedDateFrom: null,
	updatedDateTo: null,
};

export const SalesOrderFilters: React.FC<SalesOrderFiltersProps> = ({
	onFiltersChange,
	onClearFilters,
	channels,
	initialFilters = {},
	initialEndpointFilters = {},
}) => {
	const { auth } = useSelector((state: RootState) => state);
	const currentUserName = auth.user ? auth.user.name : '';

	const [filters, setFilters] = useState<SalesOrderFiltersState>({
		...defaultFilters,
		...initialFilters,
	});
	const [expanded, setExpanded] = useState(false);
	const [activeFiltersCount, setActiveFiltersCount] = useState(0);
	const [sellers, setSellers] = useState<IUserSummary[]>([]);
	const prevFiltersRef = useRef<string>(
		Object.keys(initialEndpointFilters).length > 0 ? JSON.stringify(initialEndpointFilters) : '',
	);
	const onFiltersChangeRef = useRef(onFiltersChange);
	const currentUserNameRef = useRef(currentUserName);

	// Actualizar refs cuando cambian
	useEffect(() => {
		onFiltersChangeRef.current = onFiltersChange;
		currentUserNameRef.current = currentUserName;
	}, [onFiltersChange, currentUserName]);

	// Cargar lista de vendedores
	useEffect(() => {
		const loadSellers = async () => {
			try {
				const sellers = await fetchUserSummaryCached();
				// Ordenar por nombre alfabéticamente
				const sortedSellers = sellers.sort((a, b) => a.name.localeCompare(b.name));
				setSellers(sortedSellers);
			} catch (error) {
				console.error('Error al cargar vendedores:', error);
			}
		};
		loadSellers();
	}, []);

	// Contar filtros activos
	const countActiveFilters = useCallback((currentFilters: SalesOrderFiltersState) => {
		let count = 0;
		Object.entries(currentFilters).forEach(([key, value]) => {
			if (key === 'servicesTypes' || key === 'invoicingStatuses') {
				if (Array.isArray(value) && value.length > 0) count++;
			} else if (value && value !== '' && value !== null && value !== false) {
				count++;
			}
		});
		return count;
	}, []);

	// Memoizar opciones del Select de asesores para mantener estructura estable
	// Asegurar que siempre haya al menos una opción para mantener el Select montado
	const sellerMenuItems = useMemo(() => createMenuItems(sellers, (seller) => seller.name), [sellers]);

	// Memoizar opciones del Select de canales para mantener estructura estable
	const channelMenuItems = useMemo(() => createMenuItems(channels, (channel) => channel.id.toString()), [channels]);

	// Efecto único para manejar cambios de filtros
	useEffect(() => {
		const count = countActiveFilters(filters);
		setActiveFiltersCount(count);
		const endpointFilters = convertSalesOrderFiltersToEndpoint(filters, currentUserNameRef.current);
		const endpointFiltersString = JSON.stringify(endpointFilters);

		// Solo llamar a onFiltersChange si realmente cambió algo
		if (prevFiltersRef.current !== endpointFiltersString) {
			prevFiltersRef.current = endpointFiltersString;
			onFiltersChangeRef.current(endpointFilters);
		}
	}, [filters, countActiveFilters]);

	const handleFilterChange = <K extends keyof SalesOrderFiltersState>(field: K, value: SalesOrderFiltersState[K]) => {
		setFilters((prev) => {
			const newFilters = {
				...prev,
				[field]: value,
			};

			// Lógica de exclusión mutua: si se selecciona un vendedor, desactivar "Solo las mías"
			if (field === 'createdBy' && value) {
				newFilters.onlyMine = false;
			}
			// Si se activa "Solo las mías", limpiar el filtro de vendedor
			if (field === 'onlyMine' && value === true) {
				newFilters.createdBy = '';
			}

			return newFilters;
		});
	};

	const handleClearAll = () => {
		setFilters(defaultFilters);
		onClearFilters();
	};

	const handleClearField = (field: keyof SalesOrderFiltersState) => {
		const defaultValue = getDefaultFilterValue(field);
		handleFilterChange(field, defaultValue);
	};

	const handleDatePreset = (
		field: 'createdDatePreset' | 'travelDepartureDatePreset' | 'travelReturnDatePreset',
		preset: string,
	) => {
		handleFilterChange(field, preset);
		// Limpiar fechas manuales cuando se usa preset
		if (field === 'createdDatePreset') {
			handleFilterChange('createdDateFrom', null);
			handleFilterChange('createdDateTo', null);
		} else if (field === 'travelDepartureDatePreset') {
			handleFilterChange('travelDepartureDateFrom', null);
			handleFilterChange('travelDepartureDateTo', null);
		} else if (field === 'travelReturnDatePreset') {
			handleFilterChange('travelReturnDateFrom', null);
			handleFilterChange('travelReturnDateTo', null);
		}
	};

	const renderChip = (label: string, field: keyof SalesOrderFiltersState, value: any) => {
		if (!value || value === '' || value === null || (Array.isArray(value) && value.length === 0) || value === false) {
			return null;
		}

		let displayValue = value;
		if (field === 'segmentType') {
			const option = segmentTypes.find((opt) => opt.id.toString() === value.toString());
			displayValue = option?.name || value;
		} else if (field === 'servicesTypes' && Array.isArray(value)) {
			displayValue = value
				.map((id) => {
					const option = serviceTypes.find((opt) => opt.id.toString() === id.toString());
					return option?.name || id;
				})
				.join(', ');
		} else if (field === 'channelId') {
			const channel = channels.find((ch) => ch.id.toString() === value.toString());
			displayValue = channel?.name || value;
		} else if (field === 'invoicingStatuses' && Array.isArray(value)) {
			displayValue = value
				.map((id) => {
					const option = saleOrderInvoicingStatusTypes.find((opt) => opt.id.toString() === id.toString());
					return option?.name || id;
				})
				.join(', ');
		} else if (
			field === 'hasPendingPayAmount' ||
			field === 'customerEmpty' ||
			field === 'isBitrixDeal' ||
			field === 'isReturnDateReached'
		) {
			displayValue = value ? 'Sí' : 'No';
		} else if (field === 'onlyMine') {
			displayValue = 'Solo las mías';
		} else if (field === 'createdBy') {
			displayValue = value;
		} else if (field.includes('Date') && value instanceof Date) {
			displayValue = value.toLocaleDateString('es-ES');
		} else if (field.includes('Preset') && value) {
			const presetLabels: Record<string, string> = {
				today: 'Hoy',
				thisWeek: 'Esta semana',
				thisMonth: 'Este mes',
				last30Days: 'Últimos 30 días',
				next10Days: 'Próximos 10 días',
				next20Days: 'Próximos 20 días',
				next30Days: 'Próximos 30 días',
			};
			displayValue = presetLabels[value] || value;
		}

		return (
			<Chip
				key={field}
				label={`${label}: ${displayValue}`}
				onDelete={() => handleClearField(field)}
				color="primary"
				variant="outlined"
				size="small"
				sx={{ mr: 1, mb: 0.5 }}
			/>
		);
	};

	return (
		<Paper elevation={2} sx={{ p: 2, mb: 2, mt: 3 }}>
			{/* Header */}
			<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<Badge badgeContent={activeFiltersCount} color="primary">
						<FilterListIcon color="action" />
					</Badge>
					<Typography variant="subtitle1" fontWeight={500} component="h2">
						Filtros:
					</Typography>
				</Box>

				{/* Chips de filtros activos */}
				{activeFiltersCount > 0 && (
					<Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5} flex={1} justifyContent="left">
						{renderChip('Solo las mías', 'onlyMine', filters.onlyMine)}
						{renderChip('Asesor', 'createdBy', filters.createdBy)}
						{renderChip('Deal ID', 'dealId', filters.dealId)}
						{renderChip('Segmento', 'segmentType', filters.segmentType)}
						{renderChip('Facturación', 'invoicingStatuses', filters.invoicingStatuses)}
						{renderChip('Tiene saldo pendiente', 'hasPendingPayAmount', filters.hasPendingPayAmount)}
						{renderChip('Cliente vacío', 'customerEmpty', filters.customerEmpty)}
						{renderChip('Pasajero principal', 'mainPassengerCompleteName', filters.mainPassengerCompleteName)}
						{renderChip('Cliente', 'customerCompleteName', filters.customerCompleteName)}
						{renderChip('Fecha creación', 'createdDatePreset', filters.createdDatePreset)}
						{renderChip('Fecha ida', 'travelDepartureDatePreset', filters.travelDepartureDatePreset)}
						{renderChip('Fecha vuelta', 'travelReturnDatePreset', filters.travelReturnDatePreset)}
						{renderChip('Número de Orden', 'orderNumber', filters.orderNumber)}
						{renderChip('Quotations ID', 'quotationId', filters.quotationId)}
						{renderChip('Servicios', 'servicesTypes', filters.servicesTypes)}
						{renderChip('Canal', 'channelId', filters.channelId)}
						{renderChip('Es un Deal', 'isBitrixDeal', filters.isBitrixDeal)}
						{renderChip('Viaje finalizado', 'isReturnDateReached', filters.isReturnDateReached)}
					</Box>
				)}

				<Box display="flex" gap={1}>
					<Tooltip title="Limpiar todos los filtros">
						<Button size="small" startIcon={<ClearIcon />} onClick={handleClearAll} variant="outlined" color="error">
							Limpiar
						</Button>
					</Tooltip>
					<Tooltip title={expanded ? 'Ocultar filtros avanzados' : 'Mostrar más filtros'}>
						<Button
							size="small"
							endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
							onClick={() => setExpanded(!expanded)}
							variant="outlined"
							color="primary"
							sx={{
								fontWeight: 500,
								borderWidth: 1.5,
								'&:hover': {
									borderWidth: 1.5,
									backgroundColor: 'primary.light',
									color: 'primary.contrastText',
								},
							}}
						>
							{expanded ? 'Menos filtros' : 'Más filtros'}
						</Button>
					</Tooltip>
				</Box>
			</Box>

			{/* Filtros rápidos - Siempre visibles */}
			<Grid container spacing={1.5} sx={{ mb: 1 }}>
				<Grid item xs={12} sm={6} md={2}>
					<FormControlLabel
						control={
							<Switch
								checked={filters.onlyMine}
								onChange={(e) => handleFilterChange('onlyMine', e.target.checked)}
								color="primary"
							/>
						}
						label="Solo las mías"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel>Asesor</InputLabel>
						<Select
							value={filters.createdBy}
							onChange={(e) => handleFilterChange('createdBy', e.target.value)}
							label="Asesor"
						>
							{sellerMenuItems}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={2}>
					<TextField
						fullWidth
						size="small"
						label="Deal ID"
						value={filters.dealId}
						onChange={(e) => handleFilterChange('dealId', e.target.value)}
						type="number"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel>Segmento</InputLabel>
						<Select
							value={filters.segmentType}
							onChange={(e) => handleFilterChange('segmentType', e.target.value)}
							label="Segmento"
						>
							<MenuItem value="">
								<em>Todos</em>
							</MenuItem>
							{segmentTypes.map((option) => (
								<MenuItem key={option.id} value={option.id.toString()}>
									{option.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={2}>
					<FormControl fullWidth size="small">
						<InputLabel>Facturación</InputLabel>
						<Select
							multiple
							value={filters.invoicingStatuses}
							onChange={(e) => handleFilterChange('invoicingStatuses', e.target.value as string[])}
							label="Facturación"
							renderValue={(selected) =>
								selected
									.map((id) => {
										const option = saleOrderInvoicingStatusTypes.find((opt) => opt.id.toString() === id.toString());
										return option?.name || id;
									})
									.join(', ')
							}
						>
							{saleOrderInvoicingStatusTypes.map((option) => (
								<MenuItem key={option.id} value={option.id.toString()}>
									{option.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={2}>
					<FormControlLabel
						control={
							<Switch
								checked={filters.hasPendingPayAmount ?? false}
								onChange={(e) => handleFilterChange('hasPendingPayAmount', e.target.checked ? true : null)}
								color="primary"
							/>
						}
						label="Tiene saldo pendiente"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={2}>
					<FormControlLabel
						control={
							<Switch
								checked={filters.customerEmpty ?? false}
								onChange={(e) => handleFilterChange('customerEmpty', e.target.checked ? true : null)}
								color="primary"
							/>
						}
						label="Cliente vacío"
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={5}>
					<TextField
						fullWidth
						size="small"
						label="Pasajero principal contiene"
						value={filters.mainPassengerCompleteName}
						onChange={(e) => handleFilterChange('mainPassengerCompleteName', e.target.value)}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={5}>
					<TextField
						fullWidth
						size="small"
						label="Cliente contiene"
						value={filters.customerCompleteName}
						onChange={(e) => handleFilterChange('customerCompleteName', e.target.value)}
					/>
				</Grid>
			</Grid>

			{/* Filtros de fecha - Creación (solo botones rápidos, siempre visibles) */}
			<Grid container spacing={1} sx={{ mb: 1 }}>
				<Grid item xs={12}>
					<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
						Fecha de Creación:
					</Typography>
					<ButtonGroup size="small" variant="outlined" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
						<Button
							variant={filters.createdDatePreset === 'today' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset('createdDatePreset', filters.createdDatePreset === 'today' ? '' : 'today')
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Hoy
						</Button>
						<Button
							variant={filters.createdDatePreset === 'thisWeek' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset('createdDatePreset', filters.createdDatePreset === 'thisWeek' ? '' : 'thisWeek')
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Esta semana
						</Button>
						<Button
							variant={filters.createdDatePreset === 'thisMonth' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset('createdDatePreset', filters.createdDatePreset === 'thisMonth' ? '' : 'thisMonth')
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Este mes
						</Button>
						<Button
							variant={filters.createdDatePreset === 'last30Days' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset('createdDatePreset', filters.createdDatePreset === 'last30Days' ? '' : 'last30Days')
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Últimos 30 días
						</Button>
						<Button
							variant={filters.createdDatePreset === 'lastMonth' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset('createdDatePreset', filters.createdDatePreset === 'lastMonth' ? '' : 'lastMonth')
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							El mes pasado
						</Button>
					</ButtonGroup>
				</Grid>
			</Grid>

			{/* Filtros de fecha - Ida (solo botones rápidos, siempre visibles) */}
			<Grid container spacing={1} sx={{ mb: 1 }}>
				<Grid item xs={12}>
					<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
						Fecha de Ida:
					</Typography>
					<ButtonGroup size="small" variant="outlined" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
						<Button
							variant={filters.travelDepartureDatePreset === 'today' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset(
									'travelDepartureDatePreset',
									filters.travelDepartureDatePreset === 'today' ? '' : 'today',
								)
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Viajando hoy
						</Button>
						<Button
							variant={filters.travelDepartureDatePreset === 'thisWeek' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset(
									'travelDepartureDatePreset',
									filters.travelDepartureDatePreset === 'thisWeek' ? '' : 'thisWeek',
								)
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Esta semana
						</Button>
						<Button
							variant={filters.travelDepartureDatePreset === 'thisMonth' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset(
									'travelDepartureDatePreset',
									filters.travelDepartureDatePreset === 'thisMonth' ? '' : 'thisMonth',
								)
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Este mes
						</Button>
						<Button
							variant={filters.travelDepartureDatePreset === 'next10Days' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset(
									'travelDepartureDatePreset',
									filters.travelDepartureDatePreset === 'next10Days' ? '' : 'next10Days',
								)
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Próximos 10 días
						</Button>
						<Button
							variant={filters.travelDepartureDatePreset === 'next20Days' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset(
									'travelDepartureDatePreset',
									filters.travelDepartureDatePreset === 'next20Days' ? '' : 'next20Days',
								)
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Próximos 20 días
						</Button>
						<Button
							variant={filters.travelDepartureDatePreset === 'next30Days' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset(
									'travelDepartureDatePreset',
									filters.travelDepartureDatePreset === 'next30Days' ? '' : 'next30Days',
								)
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Próximos 30 días
						</Button>
						<Button
							variant={filters.travelDepartureDatePreset === 'next45Days' ? 'contained' : 'outlined'}
							onClick={() =>
								handleDatePreset(
									'travelDepartureDatePreset',
									filters.travelDepartureDatePreset === 'next45Days' ? '' : 'next45Days',
								)
							}
							sx={{ minWidth: 'auto', px: 1.5 }}
						>
							Próximos 45 días
						</Button>
					</ButtonGroup>
				</Grid>
			</Grid>

			{/* Formulario de filtros expandible - Siempre renderizado para mantener orden de hooks */}
			<Collapse in={expanded}>
				<Divider sx={{ mb: 2, mt: 1 }} />
				<Grid container spacing={2}>
					{/* Filtros de fecha - Creación (rangos de fecha) */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" color="text.secondary" mb={1} fontWeight={600}>
							Rango de Fecha de Creación
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Fecha desde"
							value={filters.createdDateFrom}
							onChange={(date) => {
								handleFilterChange('createdDateFrom', date);
								handleFilterChange('createdDatePreset', '');
							}}
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							inputFormat="dd/MM/yyyy"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Fecha hasta"
							value={filters.createdDateTo}
							onChange={(date) => {
								handleFilterChange('createdDateTo', date);
								handleFilterChange('createdDatePreset', '');
							}}
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							inputFormat="dd/MM/yyyy"
						/>
					</Grid>

					{/* Filtros de fecha - Ida (rangos de fecha) */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" color="text.secondary" mb={1} mt={2} fontWeight={600}>
							Rango de Fecha de Ida
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Fecha desde"
							value={filters.travelDepartureDateFrom}
							onChange={(date) => {
								handleFilterChange('travelDepartureDateFrom', date);
								handleFilterChange('travelDepartureDatePreset', '');
							}}
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							inputFormat="dd/MM/yyyy"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Fecha hasta"
							value={filters.travelDepartureDateTo}
							onChange={(date) => {
								handleFilterChange('travelDepartureDateTo', date);
								handleFilterChange('travelDepartureDatePreset', '');
							}}
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							inputFormat="dd/MM/yyyy"
						/>
					</Grid>

					{/* Filtros de fecha - Vuelta */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" color="text.secondary" mb={1} mt={2} fontWeight={600}>
							Fecha de Vuelta
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<ButtonGroup size="small" variant="outlined">
							<Button
								variant={filters.travelReturnDatePreset === 'today' ? 'contained' : 'outlined'}
								onClick={() =>
									handleDatePreset('travelReturnDatePreset', filters.travelReturnDatePreset === 'today' ? '' : 'today')
								}
							>
								Viajando hoy
							</Button>
							<Button
								variant={filters.travelReturnDatePreset === 'thisWeek' ? 'contained' : 'outlined'}
								onClick={() =>
									handleDatePreset(
										'travelReturnDatePreset',
										filters.travelReturnDatePreset === 'thisWeek' ? '' : 'thisWeek',
									)
								}
							>
								Viajando esta semana
							</Button>
							<Button
								variant={filters.travelReturnDatePreset === 'thisMonth' ? 'contained' : 'outlined'}
								onClick={() =>
									handleDatePreset(
										'travelReturnDatePreset',
										filters.travelReturnDatePreset === 'thisMonth' ? '' : 'thisMonth',
									)
								}
							>
								Viajando este mes
							</Button>
							<Button
								variant={filters.travelReturnDatePreset === 'next10Days' ? 'contained' : 'outlined'}
								onClick={() =>
									handleDatePreset(
										'travelReturnDatePreset',
										filters.travelReturnDatePreset === 'next10Days' ? '' : 'next10Days',
									)
								}
							>
								Próximos 10 días
							</Button>
							<Button
								variant={filters.travelReturnDatePreset === 'next20Days' ? 'contained' : 'outlined'}
								onClick={() =>
									handleDatePreset(
										'travelReturnDatePreset',
										filters.travelReturnDatePreset === 'next20Days' ? '' : 'next20Days',
									)
								}
							>
								Próximos 20 días
							</Button>
							<Button
								variant={filters.travelReturnDatePreset === 'next30Days' ? 'contained' : 'outlined'}
								onClick={() =>
									handleDatePreset(
										'travelReturnDatePreset',
										filters.travelReturnDatePreset === 'next30Days' ? '' : 'next30Days',
									)
								}
							>
								Próximos 30 días
							</Button>
						</ButtonGroup>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Fecha desde"
							value={filters.travelReturnDateFrom}
							onChange={(date) => {
								handleFilterChange('travelReturnDateFrom', date);
								handleFilterChange('travelReturnDatePreset', '');
							}}
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							inputFormat="dd/MM/yyyy"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Fecha hasta"
							value={filters.travelReturnDateTo}
							onChange={(date) => {
								handleFilterChange('travelReturnDateTo', date);
								handleFilterChange('travelReturnDatePreset', '');
							}}
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							inputFormat="dd/MM/yyyy"
						/>
					</Grid>

					{/* Filtros avanzados - Identificadores */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" color="text.secondary" mb={1} mt={2} fontWeight={600}>
							Identificadores
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6} md={6}>
						<TextField
							fullWidth
							size="small"
							label="Número de Orden"
							value={filters.orderNumber}
							onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
							type="number"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={6}>
						<TextField
							fullWidth
							size="small"
							label="Quotations ID"
							value={filters.quotationId}
							onChange={(e) => handleFilterChange('quotationId', e.target.value)}
							type="number"
						/>
					</Grid>

					{/* Filtros avanzados - Texto */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" color="text.secondary" mb={1} mt={2} fontWeight={600}>
							Texto
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6} md={6}>
						<TextField
							fullWidth
							size="small"
							label="Destinos"
							value={filters.destinations}
							onChange={(e) => handleFilterChange('destinations', e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={6}>
						<TextField
							fullWidth
							size="small"
							label="Referencia del cliente"
							value={filters.clientReference}
							onChange={(e) => handleFilterChange('clientReference', e.target.value)}
						/>
					</Grid>

					{/* Filtros avanzados - Selects */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" color="text.secondary" mb={1} mt={2} fontWeight={600}>
							Selección
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<FormControl fullWidth size="small">
							<InputLabel>Servicios contratados</InputLabel>
							<Select
								multiple
								value={filters.servicesTypes}
								onChange={(e) => handleFilterChange('servicesTypes', e.target.value as string[])}
								label="Servicios contratados"
								renderValue={(selected) => {
									if (selected.length === 0) return '';
									return selected
										.map((id) => {
											const option = serviceTypes.find((opt) => opt.id.toString() === id.toString());
											return option?.name || id;
										})
										.join(', ');
								}}
							>
								{serviceTypes.map((option) => (
									<MenuItem key={option.id} value={option.id.toString()}>
										{option.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<FormControl fullWidth size="small">
							<InputLabel>Canal</InputLabel>
							<Select
								value={filters.channelId}
								onChange={(e) => handleFilterChange('channelId', e.target.value)}
								label="Canal"
							>
								{channelMenuItems}
							</Select>
						</FormControl>
					</Grid>
					{/* Filtros avanzados - Booleanos */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" color="text.secondary" mb={1} mt={2} fontWeight={600}>
							Booleanos
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormControlLabel
							control={
								<Switch
									checked={filters.isBitrixDeal ?? false}
									onChange={(e) => handleFilterChange('isBitrixDeal', e.target.checked ? true : null)}
									color="primary"
								/>
							}
							label="Es un Deal"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormControlLabel
							control={
								<Switch
									checked={filters.isReturnDateReached ?? false}
									onChange={(e) => handleFilterChange('isReturnDateReached', e.target.checked ? true : null)}
									color="primary"
								/>
							}
							label="Viaje finalizado"
						/>
					</Grid>

					{/* Fecha última edición */}
					<Grid item xs={12}>
						<Typography variant="subtitle2" color="text.secondary" mb={1} mt={2} fontWeight={600}>
							Última Edición
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Fecha desde"
							value={filters.updatedDateFrom}
							onChange={(date) => handleFilterChange('updatedDateFrom', date)}
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							inputFormat="dd/MM/yyyy"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DatePicker
							label="Fecha hasta"
							value={filters.updatedDateTo}
							onChange={(date) => handleFilterChange('updatedDateTo', date)}
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							inputFormat="dd/MM/yyyy"
						/>
					</Grid>
				</Grid>
			</Collapse>
		</Paper>
	);
};
